import axios from "axios";
// Helper functions
import { setToken } from "config/storageController";
// Context
import { profile } from "context/profile";

// Bare instance with no interceptors so a refresh call can never recurse into itself.
const refreshClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
  },
  validateStatus: (status) => status >= 200 && status < 500,
  withCredentials: true,
});

const REFRESH_TOKEN_PATH = "/account/refresh-token";

// Shared promise so concurrent 401s trigger a single network call.
let inFlightRefresh = null;

const requestNewAccessToken = async () => {
  let response = {
    success: false,
    message: "",
    data: null,
    accessToken: null,
  };

  try {
    const result = await refreshClient.post(REFRESH_TOKEN_PATH);

    if (result?.data?.success) {
      const data = result.data?.data ?? null;
      const accessToken = data?.accessToken || null;

      if (!accessToken) {
        response.message = "The refresh response did not include an access token.";
        return response;
      }

      if (data.staff_id) profile.staff_id = data.staff_id;
      if (data.first_name) profile.first_name = data.first_name;
      if (data.last_name) profile.last_name = data.last_name;
      if (data.role) profile.role = data.role;
      if (data.school_id) profile.school_id = data.school_id;
      profile.accessToken = accessToken;

      setToken(accessToken);

      response.success = true;
      response.message = result.data?.message ?? "Access token refreshed";
      response.data = data;
      response.accessToken = accessToken;
    } else {
      response.message =
        result?.data?.message ||
        "Your session could not be renewed. Please sign in again to continue.";
    }
  } catch (error) {
    console.error("Error refreshing access token:", error);
    response.message =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while renewing your session. Please check your connection and try again.";
  }

  return response;
};

/**
 * Requests a new access token from the refresh-token endpoint, de-duplicating
 * concurrent callers so only one network request is made at a time.
 * @returns {Promise<{success: boolean, message: string, data: any, accessToken: string|null}>} Refresh result.
 */
export const refreshAccessToken = async () => {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = requestNewAccessToken().finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
};
